import { useState } from "react";
import {
  MdCampaign,
  MdDeleteOutline,
  MdOutlineEmail,
  MdOutlineHistory,
  MdOutlineTaskAlt,
  MdPushPin,
  MdOutlinePushPin,
  MdTrendingUp,
} from "react-icons/md";
import { Badge, Card, EmptyState } from "../Primitives";
import {
  formatDate,
  formatDaysCount,
  INTERACTION_LABELS,
  INTERACTION_OUTCOMES,
  outcomeOf,
} from "../crmHelpers";

/*
  Histórico da conta: tudo o que aconteceu com ela na mesma linha do tempo, com filtro
  por assunto. Sem o filtro, as notas automáticas de cobrança afogariam o que a equipe
  conversou.
*/

const FILTERS = [
  { id: "ALL", label: "Tudo" },
  { id: "CONTACT", label: "Contatos" },
  { id: "BILLING", label: "Financeiro" },
  { id: "MESSAGE", label: "Mensagens" },
  { id: "SYSTEM", label: "Alterações" },
];

const TYPE_TONE = {
  CALL: "info",
  WHATSAPP: "success",
  EMAIL: "info",
  MEETING: "warning",
  NOTE: "neutral",
  SYSTEM: "neutral",
};

const DELIVERY_TEXT = {
  QUEUED: "na fila de envio",
  SENT: "enviado",
  FAILED: "não entregue",
};

const Marker = ({ className, children }) => (
  <span
    className={`absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full ${className}`}
  >
    {children}
  </span>
);

const emailSummary = (item) => {
  if (item.clickedAt) return `clicou em ${formatDate(item.clickedAt)}`;
  if (item.openedAt) return `abriu em ${formatDate(item.openedAt)}`;
  return DELIVERY_TEXT[item.status] || item.status;
};

const TimelineItem = ({
  item,
  stageLabel,
  canDelete,
  onTogglePin,
  onDelete,
  onOutcome,
}) => {
  if (item.kind === "STAGE") {
    return (
      <li className="relative pl-6">
        <Marker className="bg-slate-100 text-slate-400">
          <MdTrendingUp size={11} />
        </Marker>
        <p className="text-sm text-slate-700">
          Estágio:{" "}
          <span className="font-semibold">
            {item.previousStage ? stageLabel(item.previousStage) : "primeira classificação"}
          </span>{" "}
          → <span className="font-semibold">{stageLabel(item.stage)}</span>
          {item.daysInPreviousStage !== null && item.daysInPreviousStage !== undefined
            ? ` após ${formatDaysCount(item.daysInPreviousStage)}`
            : ""}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} · {item.authorName}
        </p>
      </li>
    );
  }

  if (item.kind === "TASK") {
    return (
      <li className="relative pl-6">
        <Marker
          className={
            item.status === "DONE"
              ? "bg-emerald-50 text-emerald-500"
              : "bg-sky-50 text-sky-500"
          }
        >
          <MdOutlineTaskAlt size={11} />
        </Marker>
        <p className="text-sm text-slate-800">
          <span className="font-semibold">Tarefa</span> · {item.title}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} ·{" "}
          {item.status === "OPEN"
            ? `aberta para ${item.assigneeName}`
            : item.status === "DONE"
            ? "concluída"
            : "cancelada"}
        </p>
      </li>
    );
  }

  if (item.kind === "EMAIL") {
    return (
      <li className="relative pl-6">
        <Marker className="bg-sky-50 text-sky-500">
          <MdOutlineEmail size={11} />
        </Marker>
        <p className="text-sm text-slate-800">
          <span className="font-semibold">E-mail</span> · {item.title}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} · {item.origin} · {emailSummary(item)}
        </p>
      </li>
    );
  }

  if (item.kind === "ANNOUNCEMENT") {
    return (
      <li className="relative pl-6">
        <Marker className="bg-violet-50 text-violet-500">
          <MdCampaign size={11} />
        </Marker>
        <p className="text-sm text-slate-800">
          <span className="font-semibold">Comunicado</span> · {item.title}
        </p>
        <p className="text-[11px] text-slate-400">
          {formatDate(item.at)} ·{" "}
          {item.readCount > 0
            ? `lido por ${item.readCount} pessoa(s)`
            : "ainda não lido"}
        </p>
      </li>
    );
  }

  const outcome = outcomeOf(item.outcome);
  const isHuman = item.type !== "SYSTEM";

  return (
    <li className="group relative pl-6">
      <span className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-white bg-pink-400 ring-1 ring-pink-100" />
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="mb-0.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={TYPE_TONE[item.type]}>
              {INTERACTION_LABELS[item.type] || item.type}
            </Badge>
            {outcome ? <Badge tone={outcome.tone}>{outcome.label}</Badge> : null}
            {item.pinned ? (
              <MdPushPin size={12} className="text-pink-600" title="Fixada" />
            ) : null}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm text-slate-800">
            {item.body}
          </p>
          <p className="text-[11px] text-slate-400">
            {formatDate(item.at)} · {item.authorName}
          </p>

          {/* Contato antigo sem resultado ainda pode ganhar um. Nota não tem resultado. */}
          {isHuman && !outcome && item.type !== "NOTE" ? (
            <select
              value=""
              aria-label="Como foi o contato"
              onChange={(event) =>
                onOutcome({ interactionId: item.id, outcome: event.target.value })
              }
              className="mt-1 rounded-md border-0 bg-transparent py-0 pl-0 pr-6 text-[11px] font-medium text-pink-700 focus:ring-0"
            >
              <option value="" disabled>
                Como foi?
              </option>
              {INTERACTION_OUTCOMES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        {isHuman ? (
          <div className="flex shrink-0 gap-0.5 opacity-100 transition lg:opacity-0 lg:group-hover:opacity-100">
            <button
              type="button"
              onClick={() => onTogglePin(item.id, !item.pinned)}
              title={item.pinned ? "Desafixar" : "Fixar no topo"}
              className={`rounded-md p-1 transition hover:bg-pink-50 ${
                item.pinned ? "text-pink-600" : "text-slate-300 hover:text-pink-600"
              }`}
            >
              {item.pinned ? <MdPushPin size={15} /> : <MdOutlinePushPin size={15} />}
            </button>
            {canDelete(item) ? (
              <button
                type="button"
                onClick={() => onDelete(item.id)}
                title="Apagar"
                className="rounded-md p-1 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600"
              >
                <MdDeleteOutline size={15} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
};

const AccountTimeline = ({
  timeline = [],
  stageLabel,
  canDelete,
  onTogglePin,
  onDelete,
  onOutcome,
}) => {
  const [filter, setFilter] = useState("ALL");

  const counts = timeline.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {});
  const visible =
    filter === "ALL"
      ? timeline
      : timeline.filter((item) => item.category === filter);

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-bold text-slate-900">Histórico</h2>
        <div className="mt-2 flex flex-wrap gap-1">
          {FILTERS.map((option) => {
            const total = option.id === "ALL" ? timeline.length : counts[option.id] || 0;

            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={filter === option.id}
                onClick={() => setFilter(option.id)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset transition ${
                  filter === option.id
                    ? "bg-pink-600 text-white ring-pink-600"
                    : "bg-white text-slate-600 ring-slate-200 hover:ring-pink-200"
                }`}
              >
                {option.label}
                {total ? ` ${total}` : ""}
              </button>
            );
          })}
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={MdOutlineHistory}
          title={timeline.length ? "Nada neste filtro" : "Nada registrado ainda"}
          description={
            timeline.length
              ? "Escolha outro filtro para ver o resto do histórico."
              : "Registre o primeiro contato. A partir daí o histórico fica com a conta, e não com quem atendeu."
          }
        />
      ) : (
        /*
          Trilho vertical em vez de linhas separadas: a timeline passa a se
          ler como uma sequência, e não como uma lista de registros soltos.
        */
        <ul className="relative space-y-4 px-4 py-4 before:absolute before:bottom-4 before:left-[23px] before:top-4 before:w-px before:bg-slate-200">
          {visible.map((item) => (
            <TimelineItem
              key={`${item.kind}-${item.id}`}
              item={item}
              stageLabel={stageLabel}
              canDelete={canDelete}
              onTogglePin={onTogglePin}
              onDelete={onDelete}
              onOutcome={onOutcome}
            />
          ))}
        </ul>
      )}
    </Card>
  );
};

export default AccountTimeline;
